import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, ShieldCheck, FileText, Sparkles, ChevronRight, TrendingUp, Bell, Trophy, Gift, Leaf, HeartPulse } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [claims, setClaims] = useState([]);
  const [subs, setSubs] = useState([]);
  const [notifs, setNotifs] = useState([]);

  const load = useCallback(async () => {
    await Promise.all([
      api.get("/devices").then(r => setDevices(r.data)).catch(() => {}),
      api.get("/claims").then(r => setClaims(r.data)).catch(() => {}),
      api.get("/subscriptions").then(r => setSubs(r.data)).catch(() => {}),
      api.get("/notifications").then(r => setNotifs(r.data)).catch(() => {}),
    ]);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeSub = subs.find(s => s.status === "active");
  const openClaims = claims.filter(c => !["closed", "returned"].includes(c.status));

  return (
    <div className="space-y-8">
      {/* Greeting */}
      <div>
        <div className="overline text-secondary">Welcome back</div>
        <h1 className="mt-1 font-display text-4xl lg:text-5xl font-light tracking-tight">Hi {user?.name?.split(" ")[0]}.</h1>
        <p className="mt-2 text-muted-foreground">Here's a snapshot of your treasure.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Smartphone, label: "Devices", value: devices.length, to: "/app/devices", tone: "primary" },
          { icon: ShieldCheck, label: "Active plan", value: activeSub ? activeSub.plan_id.toUpperCase() : "None", to: "/app/subscription", tone: "secondary" },
          { icon: FileText, label: "Open claims", value: openClaims.length, to: "/app/claims", tone: "primary" },
          { icon: TrendingUp, label: "Total claims", value: claims.length, to: "/app/claims", tone: "muted" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <Link key={s.label} to={s.to} className="bg-card border border-border rounded-2xl p-5 hover:-translate-y-1 transition-transform group">
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.tone === "secondary" ? "bg-secondary/15 text-secondary" : "bg-primary/8 text-primary"}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="mt-4 overline text-muted-foreground">{s.label}</div>
              <div className="mt-1 font-display text-3xl font-medium">{s.value}</div>
            </Link>
          );
        })}
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent claims */}
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline text-secondary">Recent claims</div>
              <div className="font-display text-2xl mt-1">Repair activity</div>
            </div>
            <Link to="/app/claims"><Button variant="ghost" size="sm">View all <ChevronRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
          <div className="mt-6 space-y-3">
            {claims.length === 0 && (
              <div className="text-center py-10 text-sm text-muted-foreground">
                No claims yet. When damage happens, we'll be here.
              </div>
            )}
            {claims.slice(0,5).map(c => (
              <Link key={c.id} to={`/app/claims/${c.id}`} className="flex items-center justify-between p-4 bg-muted/40 rounded-xl hover:bg-muted transition-colors">
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.case_number} · {c.issue_category}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{c.description}</div>
                </div>
                <span className={`text-[10px] overline px-2 py-1 rounded-full ${
                  c.status === "closed" ? "bg-primary/10 text-primary" :
                  c.status === "returned" ? "bg-secondary/20 text-secondary" :
                  "bg-primary text-primary-foreground"
                }`}>{c.status.replace("_"," ")}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Sub + quick actions */}
        <div className="space-y-6">
          <div className="bg-primary text-primary-foreground rounded-2xl p-6 shadow-teal-glow relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-40" />
            <div className="relative">
              <div className="overline text-secondary">Membership</div>
              {activeSub ? (
                <>
                  <div className="mt-1 font-display text-3xl capitalize">{activeSub.plan_id} plan</div>
                  <div className="mt-1 text-primary-foreground/70 text-xs">ID {activeSub.membership_id}</div>
                  <div className="mt-4 text-xs text-primary-foreground/70">Renews {activeSub.renews_at ? new Date(activeSub.renews_at).toLocaleDateString() : "—"}</div>
                </>
              ) : (
                <>
                  <div className="mt-1 font-display text-2xl">No active plan</div>
                  <Link to="/app/subscription"><Button className="mt-4 bg-secondary text-teal hover:bg-secondary/90">Pick a plan</Button></Link>
                </>
              )}
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center gap-2 overline text-secondary"><Sparkles className="w-3 h-3" /> Quick actions</div>
            <div className="mt-4 space-y-2">
              <Link to="/app/devices"><Button variant="outline" className="w-full justify-between">Register a device <ChevronRight className="w-4 h-4" /></Button></Link>
              <Link to="/app/claims/new"><Button variant="outline" className="w-full justify-between">File a claim <ChevronRight className="w-4 h-4" /></Button></Link>
              <Link to="/app/notifications"><Button variant="outline" className="w-full justify-between"><span className="flex items-center gap-2"><Bell className="w-3.5 h-3.5" /> Notifications</span> <ChevronRight className="w-4 h-4" /></Button></Link>
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center gap-2 overline text-secondary"><Sparkles className="w-3 h-3" /> What's new</div>
            <div className="mt-4 space-y-2">
              <Link to="/app/impact" data-testid="dash-impact-link"><Button variant="outline" className="w-full justify-between"><span className="flex items-center gap-2"><Leaf className="w-3.5 h-3.5 text-emerald-500" /> My CO₂ impact</span><ChevronRight className="w-4 h-4" /></Button></Link>
              <Link to="/app/challenges" data-testid="dash-challenges-link"><Button variant="outline" className="w-full justify-between"><span className="flex items-center gap-2"><Trophy className="w-3.5 h-3.5 text-amber-500" /> Challenges & badges</span><ChevronRight className="w-4 h-4" /></Button></Link>
              <Link to="/app/gifts" data-testid="dash-gifts-link"><Button variant="outline" className="w-full justify-between"><span className="flex items-center gap-2"><Gift className="w-3.5 h-3.5 text-rose-500" /> Send a gift card</span><ChevronRight className="w-4 h-4" /></Button></Link>
              <Link to="/app/health-score" data-testid="dash-health-link"><Button variant="outline" className="w-full justify-between"><span className="flex items-center gap-2"><HeartPulse className="w-3.5 h-3.5 text-primary" /> Device health score</span><ChevronRight className="w-4 h-4" /></Button></Link>
              <Link to="/app/wrapped" data-testid="dash-wrapped-link"><Button variant="outline" className="w-full justify-between"><span className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-secondary" /> My Wrapped {new Date().getFullYear()}</span><ChevronRight className="w-4 h-4" /></Button></Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
